import React, { useState } from "react";
import {
  Plus,
  Star,
  Pencil,
  Copy,
  ArrowUp,
  ArrowDown,
  GripVertical,
  Trash2,
  Download,
  Upload,
  Save,
  X,
  Video as VideoIcon,
  Package,
  Search,
} from "lucide-react";
import {
  askConfirm,
  Button,
  Field,
  Modal,
  PageHead,
  SearchBox,
  ProductCard,
  ProductImage,
  Empty,
  download,
} from "./ui";
import { nextSku } from "./appearance";
import { api } from "./data";
import { uid, money, csvEncode, csvParse, safeUrl, date } from "./domain";
export function ProductEditor({ product, data, onClose, run }) {
  const [p, setP] = useState(
    product
      ? structuredClone(product)
      : {
          id: uid(),
          store_id: data.store.id,
          name: "",
          sku: nextSku(data.products),
          description: "",
          price: 0,
          compare_price: 0,
          wholesale_price: 0,
          wholesale_min: 6,
          images: [],
          video_url: "",
          category_id: "",
          featured: false,
          active: true,
          sort_order: data.products.length,
        },
  );
  const initial = data.variants.filter((v) => v.product_id === product?.id);
  const [variants, setVariants] = useState(
    initial.length
      ? structuredClone(initial)
      : product?.variant_templates || [
          {
            id: uid(),
            color: "Único",
            hex: "#743ed1",
            sku: nextSku(data.variants, "V"),
            stock: 0,
            low_stock: 3,
            image: "",
            active: true,
          },
        ],
  );
  const [busy, setBusy] = useState(false),
    [tab, setTab] = useState("general");
  const set = (k, v) => setP((p) => ({ ...p, [k]: v }));
  const setV = (id, k, value) =>
    setVariants((vs) =>
      vs.map((v) => (v.id === id ? { ...v, [k]: value } : v)),
    );
  async function upload(files, type, variantId) {
    setBusy(true);
    try {
      for (const file of files) {
        const url = await api.upload(file, data.store.id);
        if (variantId) setV(variantId, "image", url);
        else if (type === "video") set("video_url", url);
        else setP((p) => ({ ...p, images: [...p.images, url] }));
      }
    } catch (e) {
      await run(() => Promise.reject(e)).catch(() => {});
    } finally {
      setBusy(false);
    }
  }
  async function save(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await run(() =>
        api.saveProduct(
          { ...p, store_id: data.store.id },
          variants.map((v, index) => ({
            ...v,
            sku: v.sku || `${p.sku}-${index + 1}`,
          })),
        ),
      );
      onClose();
    } catch {
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={product ? "Editar producto" : "Crear producto"}
      onClose={onClose}
      wide
    >
      <form onSubmit={save}>
        <div className="tabs">
          {[
            ["general", "Información"],
            ["media", "Fotos y video"],
            ["variants", "Colores y stock"],
          ].map(([id, label]) => (
            <button
              type="button"
              key={id}
              className={tab === id ? "active" : ""}
              onClick={() => setTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <div hidden={tab !== "general"}>
          <div className="form-grid">
            <Field
              label="Nombre del producto"
              required
              maxLength={180}
              value={p.name}
              onChange={(e) => set("name", e.target.value)}
            />
            <Field
              label="Código del producto (SKU)"
              hint="Se genera automáticamente. Puedes cambiarlo por otro código único."
              required
              maxLength={60}
              value={p.sku}
              onChange={(e) => set("sku", e.target.value.toUpperCase())}
            />
            <Field label="Categoría / subcategoría">
              <select
                value={p.category_id || ""}
                onChange={(e) => set("category_id", e.target.value)}
              >
                <option value="">Sin categoría</option>
                {data.categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.parent_id ? "↳ " : ""}
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              label="Precio de venta (S/)"
              type="number"
              required
              min="0"
              step="0.01"
              value={p.price}
              onChange={(e) => set("price", Number(e.target.value))}
            />
            <Field
              label="Precio anterior (opcional)"
              hint="Si es mayor al de venta, aparece tachado y se muestra la oferta."
              type="number"
              min="0"
              step="0.01"
              value={p.compare_price}
              onChange={(e) => set("compare_price", Number(e.target.value))}
            />
            <Field
              label="Precio mayorista (0 = desactivado)"
              type="number"
              min="0"
              max={p.price || undefined}
              step="0.01"
              value={p.wholesale_price}
              onChange={(e) => set("wholesale_price", Number(e.target.value))}
            />
            <Field
              label="Cantidad mínima mayorista"
              type="number"
              min="1"
              step="1"
              value={p.wholesale_min}
              onChange={(e) => set("wholesale_min", Number(e.target.value))}
            />
          </div>
          <Field label="Descripción">
            <textarea
              maxLength={5000}
              value={p.description}
              onChange={(e) => set("description", e.target.value)}
            />
          </Field>
          <div className="flex">
            <label className="check-line">
              <input
                type="checkbox"
                checked={p.active}
                onChange={(e) => set("active", e.target.checked)}
              />{" "}
              Visible en la tienda
            </label>
            <label className="check-line">
              <input
                type="checkbox"
                checked={p.featured}
                onChange={(e) => set("featured", e.target.checked)}
              />{" "}
              Producto destacado
            </label>
          </div>
        </div>
        <div hidden={tab !== "media"}>
          <p>
            La fotografía y el video aparecen juntos en el detalle del producto.
            Puedes añadir una foto distinta a cada color.
          </p>
          <div className="media-list">
            {p.images.map((src, i) => (
              <div className="media-item" key={i}>
                <img src={safeUrl(src)} alt={`Foto ${i + 1}`} />
                <button
                  type="button"
                  aria-label={`Quitar foto ${i + 1}`}
                  onClick={() =>
                    set(
                      "images",
                      p.images.filter((_, j) => i !== j),
                    )
                  }
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
          <label className="upload-control">
            <Upload size={20} />
            <span>Subir fotografías</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              multiple
              disabled={busy}
              onChange={(e) => upload([...e.target.files], "image")}
            />
          </label>
          <Field
            label="Añadir foto desde una URL HTTPS"
            type="url"
            placeholder="https://…"
            onBlur={(e) => {
              if (safeUrl(e.target.value)) {
                setP((p) => ({ ...p, images: [...p.images, e.target.value] }));
                e.target.value = "";
              }
            }}
          />
          <label className="upload-control">
            <VideoIcon size={20} />
            <span>Subir video</span>
            <input
              type="file"
              accept="video/mp4,video/webm"
              disabled={busy}
              onChange={(e) => upload([...e.target.files], "video")}
            />
          </label>
          <Field
            label="URL del video o YouTube"
            value={p.video_url}
            onChange={(e) => set("video_url", e.target.value)}
            hint="MP4 / WebM o enlace youtube.com/watch?v=… / youtu.be/…"
          />
          <p className="file-note">
            Fotografías hasta 10 MB; videos hasta 40 MB. En la demostración:
            hasta 2 MB por archivo.
          </p>
        </div>
        <div hidden={tab !== "variants"}>
          <p>
            Un código y una cantidad por cada color. Las salidas y entradas se
            registran en Inventario.
          </p>
          {variants.map((v) => (
            <div className="variant-form" key={v.id}>
              <div className="form-grid">
                <Field
                  label="Color / variante"
                  required
                  value={v.color}
                  onChange={(e) => setV(v.id, "color", e.target.value)}
                />
                <Field
                  label="Código de identificación"
                  value={v.sku}
                  placeholder={`${p.sku}-${v.color.substring(0, 3).toUpperCase()}`}
                  onChange={(e) =>
                    setV(v.id, "sku", e.target.value.toUpperCase())
                  }
                />
                <Field
                  label="Muestra"
                  type="color"
                  value={v.hex}
                  onChange={(e) => setV(v.id, "hex", e.target.value)}
                />
                <Field
                  label={
                    initial.some((x) => x.id === v.id)
                      ? "Stock actual (usar Inventario para ajustar)"
                      : "Stock inicial"
                  }
                  type="number"
                  min="0"
                  step="1"
                  disabled={initial.some((x) => x.id === v.id)}
                  value={v.stock}
                  onChange={(e) => setV(v.id, "stock", Number(e.target.value))}
                />
                <Field
                  label="Avisar cuando queden"
                  type="number"
                  min="0"
                  step="1"
                  value={v.low_stock}
                  onChange={(e) =>
                    setV(v.id, "low_stock", Number(e.target.value))
                  }
                />
              </div>
              <div className="flex">
                <label className="upload-control">
                  <span>Foto de este color</span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    disabled={busy}
                    onChange={(e) => upload([...e.target.files], "image", v.id)}
                  />
                </label>
                {v.image && (
                  <img
                    src={safeUrl(v.image)}
                    width="50"
                    height="50"
                    alt={v.color}
                  />
                )}
              </div>
              <label className="check-line">
                <input
                  type="checkbox"
                  checked={v.active}
                  onChange={(e) => setV(v.id, "active", e.target.checked)}
                />{" "}
                Color disponible en catálogo
              </label>
              {!initial.some((x) => x.id === v.id) && variants.length > 1 && (
                <Button
                  type="button"
                  kind="danger"
                  onClick={() =>
                    setVariants(variants.filter((x) => x.id !== v.id))
                  }
                >
                  Quitar variante
                </Button>
              )}
            </div>
          ))}
          <Button
            type="button"
            kind="outline"
            onClick={() =>
              setVariants([
                ...variants,
                {
                  id: uid(),
                  color: "",
                  hex: "#743ed1",
                  sku: nextSku([...data.variants, ...variants], "V"),
                  stock: 0,
                  low_stock: 3,
                  image: "",
                  active: true,
                },
              ])
            }
          >
            <Plus size={17} /> Añadir color
          </Button>
        </div>
        <div
          className="actions"
          style={{ marginTop: 24, justifyContent: "flex-end" }}
        >
          <Button type="button" kind="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            disabled={busy}
            onClick={() => {
              if (!p.name || !p.sku) setTab("general");
              else if (variants.some((v) => !v.color)) setTab("variants");
            }}
          >
            <Save size={17} />
            {busy ? "Guardando…" : "Guardar producto"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
export function Products({ data, run, view = "products" }) {
  const [query, setQuery] = useState(""),
    [category, setCategory] = useState("all"),
    [edit, setEdit] = useState(undefined),
    [featured, setFeatured] = useState(false),
    [drag, setDrag] = useState(null);
  const filtered = data.products.filter(
    (p) =>
      (!featured || p.featured) &&
      (category === "all" ||
        p.category_id === category ||
        data.categories.find((c) => c.id === p.category_id)?.parent_id ===
          category) &&
      `${p.name} ${p.sku}`.toLowerCase().includes(query.toLowerCase()),
  );
  const update = (p) =>
    run(() =>
      api.saveProduct(
        p,
        data.variants.filter((v) => v.product_id === p.id),
      ),
    ).catch(() => {});
  const move = async (id, target) => {
    const ids = data.products.map((p) => p.id);
    const from = ids.indexOf(id),
      to = ids.indexOf(target);
    ids.splice(from, 1);
    ids.splice(to, 0, id);
    await run(() => api.reorder(ids)).catch(() => {});
  };
  function duplicate(p) {
    setEdit({
      ...p,
      id: uid(),
      sku: nextSku(data.products),
      name: `${p.name} (copia)`,
      variant_templates: data.variants
        .filter((v) => v.product_id === p.id)
        .map((v, i) => ({
          ...v,
          id: uid(),
          stock: 0,
          sku: `${nextSku(data.products)}-${i + 1}`,
        })),
      active: false,
      featured: false,
      sort_order: data.products.length,
    });
  }
  return (
    <>
      <PageHead
        eyebrow="TU CATÁLOGO"
        title={
          view === "sort"
            ? "Ordenar productos"
            : view === "editor"
              ? "Editor de productos"
              : "Mis productos"
        }
        description={
          view === "sort"
            ? "Arrastra los productos o usa las flechas para cambiar su orden."
            : `${data.products.length} productos · ${data.variants.reduce((s, v) => s + v.stock, 0)} unidades en inventario`
        }
        actions={
          <>
            <a className="btn outline" href="#admin/categories">
              Categorías
            </a>
            <Button onClick={() => setEdit(null)}>
              <Plus size={17} /> Crear producto
            </Button>
          </>
        }
      />
      <div className="toolbar">
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Buscar por nombre o código"
        />
        <select
          className="control"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          <option value="all">Todas las categorías</option>
          {data.categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <Button
          kind={featured ? "" : "outline"}
          onClick={() => setFeatured(!featured)}
        >
          <Star size={16} /> Destacados
        </Button>
      </div>
      {view === "sort" ? (
        <div className="order-list">
          {filtered.map((p, i) => (
            <div
              key={p.id}
              className="sortable-row"
              draggable
              onDragStart={() => setDrag(p.id)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (drag && drag !== p.id) move(drag, p.id);
                setDrag(null);
              }}
            >
              <GripVertical size={21} />
              <ProductImage product={p} />
              <strong>{p.name}</strong>
              <span>{money(p.price)}</span>
              <div className="actions">
                <button
                  className="icon-btn"
                  aria-label={`Subir ${p.name}`}
                  disabled={i === 0}
                  onClick={() => move(p.id, filtered[i - 1].id)}
                >
                  <ArrowUp size={17} />
                </button>
                <button
                  className="icon-btn"
                  aria-label={`Bajar ${p.name}`}
                  disabled={i === filtered.length - 1}
                  onClick={() => move(p.id, filtered[i + 1].id)}
                >
                  <ArrowDown size={17} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : view === "editor" ? (
        <div className="card table-wrap editor-table">
          <table>
            <thead>
              <tr>
                <th>Producto</th>
                <th>SKU</th>
                <th>Precio de venta</th>
                <th>Precio anterior</th>
                <th>Mayorista</th>
                <th>Video / colores</th>
                <th>Guardar</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <EditorRow
                  key={`${p.id}-${p.name}-${p.price}-${p.compare_price}-${p.wholesale_price}`}
                  p={p}
                  onEdit={() => setEdit(p)}
                  onSave={update}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="products-grid admin-products">
          {filtered.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              variants={data.variants}
              onSelect={setEdit}
              adminActions={
                <>
                  <span className="stock-text">
                    {p.sku} ·{" "}
                    {data.variants
                      .filter((v) => v.product_id === p.id)
                      .reduce((s, v) => s + v.stock, 0)}{" "}
                    en stock
                  </span>
                  <div className="admin-card-actions">
                    <button
                      className="icon-btn"
                      aria-label={`Destacar ${p.name}`}
                      onClick={() => update({ ...p, featured: !p.featured })}
                    >
                      <Star
                        size={18}
                        fill={p.featured ? "#e8b247" : "none"}
                        color={p.featured ? "#e8b247" : undefined}
                      />
                    </button>
                    <button
                      className="icon-btn"
                      aria-label={`Duplicar ${p.name}`}
                      onClick={() => duplicate(p)}
                    >
                      <Copy size={17} />
                    </button>
                    <button
                      className="icon-btn"
                      aria-label={`Editar ${p.name}`}
                      onClick={() => setEdit(p)}
                    >
                      <Pencil size={17} />
                    </button>
                    <button
                      className={`switch ${p.active ? "on" : ""}`}
                      aria-label={`${p.active ? "Ocultar" : "Mostrar"} ${p.name}`}
                      onClick={() => update({ ...p, active: !p.active })}
                    >
                      <span />
                    </button>
                  </div>
                </>
              }
            />
          ))}
        </div>
      )}
      {!filtered.length && (
        <Empty
          title="Aquí empieza tu catálogo"
          action={<Button onClick={() => setEdit(null)}>Crear producto</Button>}
        />
      )}
      {edit !== undefined && (
        <ProductEditor
          product={edit}
          data={data}
          run={run}
          onClose={() => setEdit(undefined)}
        />
      )}
    </>
  );
}
function EditorRow({ p, onSave, onEdit }) {
  const [draft, setDraft] = useState(p);
  return (
    <tr>
      <td>
        <input
          className="name-input"
          aria-label={`Nombre ${p.sku}`}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </td>
      <td>
        <input
          aria-label={`Código ${p.sku}`}
          value={draft.sku}
          onChange={(e) => setDraft({ ...draft, sku: e.target.value })}
        />
      </td>
      {["price", "compare_price", "wholesale_price"].map((k) => (
        <td key={k}>
          <input
            aria-label={`${k} ${p.sku}`}
            type="number"
            min="0"
            step="0.01"
            value={draft[k]}
            onChange={(e) =>
              setDraft({ ...draft, [k]: Number(e.target.value) })
            }
          />
        </td>
      ))}
      <td>
        <Button kind="outline" onClick={onEdit}>
          <Pencil size={15} /> Editar
        </Button>
      </td>
      <td>
        <button
          className="icon-btn"
          aria-label={`Guardar ${p.sku}`}
          onClick={() => onSave(draft)}
        >
          <Save size={18} />
        </button>
      </td>
    </tr>
  );
}
export function Categories({ data, run }) {
  const [edit, setEdit] = useState(null),
    [busy, setBusy] = useState(false);
  function newCat(parent = null) {
    setEdit({
      id: uid(),
      store_id: data.store.id,
      name: "",
      parent_id: parent,
      icon: "✦",
      active: true,
      featured: true,
      sort_order: data.categories.length,
    });
  }
  const save = (c) => run(() => api.saveCategory(c)).catch(() => {});
  const ordered = data.categories
    .filter((c) => !c.parent_id)
    .flatMap((c) => [
      c,
      ...data.categories.filter((s) => s.parent_id === c.id),
    ]);
  return (
    <>
      <PageHead
        eyebrow="ORGANIZA TU TIENDA"
        title="Categorías y subcategorías"
        description="Agrupa tus productos para que sean fáciles de encontrar."
        actions={
          <>
            <Button onClick={() => newCat()}>
              <Plus size={17} /> Categoría
            </Button>
            <Button
              kind="outline"
              onClick={() =>
                newCat(data.categories.find((c) => !c.parent_id)?.id)
              }
              disabled={!data.categories.length}
            >
              <Plus size={17} /> Subcategoría
            </Button>
          </>
        }
      />
      <div className="card">
        {ordered.map((c, i) => (
          <div
            className={`category-row ${c.parent_id ? "child" : ""}`}
            key={c.id}
          >
            <div className="category-icon">{c.icon}</div>
            <strong>{c.name}</strong>
            <button
              className="icon-btn"
              aria-label={`Subir categoría ${c.name}`}
              disabled={i === 0}
              onClick={() =>
                run(async () => {
                  const prev = ordered[i - 1];
                  await api.saveCategory({ ...c, sort_order: prev.sort_order });
                  await api.saveCategory({ ...prev, sort_order: c.sort_order });
                }).catch(() => {})
              }
            >
              <ArrowUp size={16} />
            </button>
            <button
              className="icon-btn"
              aria-label={`Destacar categoría ${c.name}`}
              onClick={() => save({ ...c, featured: !c.featured })}
            >
              <Star
                size={17}
                fill={c.featured ? "#efb443" : "none"}
                color="#dab26f"
              />
            </button>
            <button
              className={`switch ${c.active ? "on" : ""}`}
              aria-label={`Cambiar visibilidad ${c.name}`}
              onClick={() => save({ ...c, active: !c.active })}
            >
              <span />
            </button>
            <button
              className="icon-btn"
              aria-label={`Editar categoría ${c.name}`}
              onClick={() => setEdit(c)}
            >
              <Pencil size={17} />
            </button>
            <button
              className="icon-btn danger"
              aria-label={`Eliminar categoría ${c.name}`}
              onClick={async () => {
                if (await askConfirm(`¿Eliminar la categoría ${c.name}?`))
                  run(() => api.deleteCategory(c.id)).catch(() => {});
              }}
            >
              <Trash2 size={17} />
            </button>
            {!c.parent_id && (
              <button
                className="icon-btn"
                aria-label={`Añadir subcategoría a ${c.name}`}
                onClick={() => newCat(c.id)}
              >
                <Plus size={17} />
              </button>
            )}
          </div>
        ))}
        {!ordered.length && <Empty title="Crea tu primera categoría" />}
      </div>
      {edit && (
        <Modal title="Editar categoría" onClose={() => setEdit(null)}>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setBusy(true);
              try {
                await run(() => api.saveCategory(edit));
                setEdit(null);
              } catch {
              } finally {
                setBusy(false);
              }
            }}
          >
            <Field
              label="Nombre"
              required
              value={edit.name}
              onChange={(e) => setEdit({ ...edit, name: e.target.value })}
            />
            <Field
              label="Símbolo"
              maxLength={4}
              value={edit.icon}
              onChange={(e) => setEdit({ ...edit, icon: e.target.value })}
            />
            <Field label="Categoría principal">
              <select
                value={edit.parent_id || ""}
                onChange={(e) =>
                  setEdit({ ...edit, parent_id: e.target.value || null })
                }
              >
                <option value="">Es una categoría principal</option>
                {data.categories
                  .filter((c) => !c.parent_id && c.id !== edit.id)
                  .map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
              </select>
            </Field>
            <Button className="full" disabled={busy}>
              Guardar categoría
            </Button>
          </form>
        </Modal>
      )}
    </>
  );
}
export function Inventory({ data, run }) {
  const [query, setQuery] = useState(""),
    [low, setLow] = useState(false),
    [adjust, setAdjust] = useState(null),
    [delta, setDelta] = useState(1),
    [reason, setReason] = useState(""),
    [view, setView] = useState("stock");
  const variants = data.variants.filter(
    (v) =>
      (!low || v.stock <= v.low_stock) &&
      `${v.sku} ${v.color} ${data.products.find((p) => p.id === v.product_id)?.name}`
        .toLowerCase()
        .includes(query.toLowerCase()),
  );
  function exportStock() {
    download(
      csvEncode([
        ["Producto", "Código", "Color", "Stock disponible", "Alerta"],
        ...variants.map((v) => [
          data.products.find((p) => p.id === v.product_id)?.name,
          v.sku,
          v.color,
          v.stock,
          v.low_stock,
        ]),
      ]),
      "inventario-dilo.csv",
      "text/csv;charset=utf-8",
    );
  }
  return (
    <>
      <PageHead
        eyebrow="CADA UNIDAD CUENTA"
        title="Inventario"
        description="Stock disponible, entradas y salidas por código y color."
        actions={
          <Button kind="outline" onClick={exportStock}>
            <Download size={17} /> Exportar inventario
          </Button>
        }
      />
      <div className="tabs">
        <button
          className={view === "stock" ? "active" : ""}
          onClick={() => setView("stock")}
        >
          Stock por color
        </button>
        <button
          className={view === "movements" ? "active" : ""}
          onClick={() => setView("movements")}
        >
          Historial de movimientos
        </button>
      </div>
      <div className="toolbar">
        <SearchBox
          value={query}
          onChange={setQuery}
          placeholder="Buscar producto, código o color"
        />
        <label className="check-line">
          <input
            type="checkbox"
            checked={low}
            onChange={(e) => setLow(e.target.checked)}
          />{" "}
          Solo stock bajo
        </label>
      </div>
      <div className="card table-wrap">
        <table>
          <thead>
            {view === "stock" ? (
              <tr>
                <th>Producto</th>
                <th>Código</th>
                <th>Color</th>
                <th>Stock</th>
                <th>Aviso</th>
                <th>Ajustar</th>
              </tr>
            ) : (
              <tr>
                <th>Fecha</th>
                <th>Código</th>
                <th>Motivo</th>
                <th>Entrada / salida</th>
                <th>Stock después</th>
              </tr>
            )}
          </thead>
          <tbody>
            {view === "stock"
              ? variants.map((v) => (
                  <tr key={v.id}>
                    <td>
                      <strong>
                        {data.products.find((p) => p.id === v.product_id)?.name}
                      </strong>
                    </td>
                    <td>{v.sku}</td>
                    <td>
                      <span style={{ color: v.hex }}>●</span> {v.color}
                    </td>
                    <td>
                      <strong
                        className={v.stock <= v.low_stock ? "error-text" : ""}
                      >
                        {v.stock}
                      </strong>
                    </td>
                    <td>
                      {v.stock <= v.low_stock
                        ? "Stock bajo"
                        : `Avisar en ${v.low_stock}`}
                    </td>
                    <td>
                      <Button
                        kind="outline"
                        onClick={() => {
                          setAdjust(v);
                          setDelta(1);
                          setReason("");
                        }}
                      >
                        <Plus size={15} /> Entrada / salida
                      </Button>
                    </td>
                  </tr>
                ))
              : data.movements
                  .filter((m) => variants.some((v) => v.id === m.variant_id))
                  .sort((a, b) => b.created_at.localeCompare(a.created_at))
                  .map((m) => (
                    <tr key={m.id}>
                      <td>{date(m.created_at)}</td>
                      <td>
                        {data.variants.find((v) => v.id === m.variant_id)?.sku}
                      </td>
                      <td>{m.reason}</td>
                      <td
                        className={
                          m.delta < 0 ? "error-text" : "stock-available"
                        }
                      >
                        {m.delta > 0 ? "+" : ""}
                        {m.delta}
                      </td>
                      <td>{m.stock_after}</td>
                    </tr>
                  ))}
          </tbody>
        </table>
        {(view === "stock" ? !variants.length : !data.movements.length) && (
          <Empty title="Sin movimientos para mostrar" />
        )}
      </div>
      {adjust && (
        <Modal
          title={`Ajustar stock · ${adjust.sku}`}
          onClose={() => setAdjust(null)}
        >
          <p>
            {adjust.color} · Disponible: {adjust.stock}
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                await run(() =>
                  api.adjustStock(adjust.id, Number(delta), reason),
                );
                setAdjust(null);
              } catch {}
            }}
          >
            <Field
              label="Cantidad a sumar o restar"
              type="number"
              step="1"
              required
              value={delta}
              onChange={(e) => setDelta(e.target.value)}
              hint="Ejemplo: 5 para ingreso, -2 para salida."
            />
            <Field
              label="Motivo"
              required
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Compra de mercadería, ajuste de conteo…"
            />
            <Button className="full">Registrar movimiento</Button>
          </form>
        </Modal>
      )}
    </>
  );
}
export function Bulk({ data, run, refresh }) {
  const [rows, setRows] = useState([]),
    [results, setResults] = useState([]),
    [busy, setBusy] = useState(false);
  const headers = [
    "sku",
    "nombre",
    "precio",
    "precio_anterior",
    "categoria",
    "color",
    "codigo_color",
    "stock_inicial",
    "foto_url",
    "video_url",
  ];
  const exportRows = () => [
    headers,
    ...data.products.flatMap((p) =>
      data.variants
        .filter((v) => v.product_id === p.id)
        .map((v) => [
          p.sku,
          p.name,
          p.price,
          p.compare_price,
          data.categories.find((c) => c.id === p.category_id)?.name || "",
          v.color,
          v.sku,
          v.stock,
          p.images[0] || "",
          p.video_url || "",
        ]),
    ),
  ];
  async function load(file) {
    try {
      const parsed = csvParse(await file.text());
      if (parsed[0]?.join(",") !== headers.join(","))
        throw new Error("Usa las columnas de la plantilla, en el mismo orden.");
      const objects = parsed.slice(1).map((r, i) => {
        if (r.length !== headers.length)
          throw new Error(`Fila ${i + 2}: faltan columnas.`);
        const o = Object.fromEntries(headers.map((k, j) => [k, r[j]]));
        if (
          !o.sku ||
          !o.nombre ||
          !o.color ||
          !o.codigo_color ||
          !Number.isFinite(Number(o.precio)) ||
          Number(o.precio) < 0 ||
          !Number.isInteger(Number(o.stock_inicial)) ||
          Number(o.stock_inicial) < 0
        )
          throw new Error(
            `Fila ${i + 2}: revisa nombre, códigos, precio y stock.`,
          );
        if (o.categoria && !data.categories.some((c) => c.name === o.categoria))
          throw new Error(
            `Fila ${i + 2}: primero crea la categoría ${o.categoria}.`,
          );
        return o;
      });
      setRows(objects);
      setResults([]);
    } catch (e) {
      run(() => Promise.reject(e)).catch(() => {});
    }
  }
  async function importRows() {
    setBusy(true);
    const out = [];
    for (const sku of [...new Set(rows.map((r) => r.sku))]) {
      try {
        const group = rows.filter((r) => r.sku === sku),
          r = group[0],
          existing = data.products.find((p) => p.sku === sku),
          id = existing?.id || uid();
        const product = {
          ...(existing || {}),
          id,
          store_id: data.store.id,
          sku,
          name: r.nombre,
          price: Number(r.precio),
          compare_price: Number(r.precio_anterior) || 0,
          category_id:
            data.categories.find((c) => c.name === r.categoria)?.id || "",
          description: existing?.description || "",
          wholesale_price: existing?.wholesale_price || 0,
          wholesale_min: existing?.wholesale_min || 6,
          images: r.foto_url ? [safeUrl(r.foto_url)] : existing?.images || [],
          video_url: safeUrl(r.video_url),
          active: existing?.active ?? true,
          featured: existing?.featured ?? false,
          sort_order: existing?.sort_order ?? data.products.length,
        };
        const variants = data.variants
          .filter((v) => v.product_id === id)
          .map((v) => ({ ...v }));
        for (const r of group) {
          const old = variants.find((v) => v.sku === r.codigo_color);
          if (old) {
            old.color = r.color;
          } else
            variants.push({
              id: uid(),
              color: r.color,
              hex: "#743ed1",
              sku: r.codigo_color,
              stock: Number(r.stock_inicial),
              low_stock: 3,
              image: "",
              active: true,
            });
        }
        await api.saveProduct(product, variants);
        out.push(`${sku}: guardado`);
      } catch (e) {
        out.push(`${sku}: ERROR — ${e.message}`);
      }
      setResults([...out]);
    }
    await refresh();
    setBusy(false);
  }
  return (
    <>
      <PageHead
        eyebrow="AHORRA TIEMPO"
        title="Creación y edición masiva"
        description="Importa productos y colores desde un CSV, o descarga tu catálogo para editarlo."
      />
      <div className="card">
        <div className="actions">
          <Button
            kind="outline"
            onClick={() =>
              download(
                csvEncode([
                  headers,
                  [
                    "BOL-001",
                    "Bolso ejemplo",
                    "45",
                    "60",
                    "",
                    "Lila",
                    "BOL-001-LIL",
                    "10",
                    "",
                    "",
                  ],
                ]),
                "plantilla-productos.csv",
                "text/csv;charset=utf-8",
              )
            }
          >
            <Download size={17} /> Plantilla CSV
          </Button>
          <Button
            kind="outline"
            onClick={() =>
              download(
                csvEncode(exportRows()),
                "productos-dilo.csv",
                "text/csv;charset=utf-8",
              )
            }
          >
            <Download size={17} /> Exportar productos
          </Button>
        </div>
        <p className="notice" style={{ marginTop: 20 }}>
          Un producto puede tener varias filas, una por color. El mismo código
          actualiza su información. Para variantes existentes el stock se
          conserva; los cambios de cantidad se hacen desde Inventario para
          mantener el historial.
        </p>
        <label className="upload-control">
          <Upload size={20} /> Elegir CSV{" "}
          <input
            type="file"
            accept=".csv"
            disabled={busy}
            onChange={(e) => e.target.files[0] && load(e.target.files[0])}
          />
        </label>
        {rows.length > 0 && (
          <>
            <h3>{rows.length} variantes listas para importar</h3>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {headers.slice(0, 8).map((h) => (
                      <th key={h}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, 50).map((r, i) => (
                    <tr key={i}>
                      {headers.slice(0, 8).map((h) => (
                        <td key={h}>{r[h]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button
              onClick={importRows}
              disabled={busy}
              style={{ marginTop: 20 }}
            >
              {busy ? "Importando…" : "Importar filas revisadas"}
            </Button>
          </>
        )}
        {results.length > 0 && (
          <div className="notice pre-line">{results.join("\n")}</div>
        )}
      </div>
    </>
  );
}
